import { useRef, useState } from 'react';
import styled, { css } from 'styled-components/macro';
import Downshift from 'downshift';
import {
  bool,
  func,
  arrayOf,
  object,
  string,
  oneOfType,
  any,
} from 'prop-types';
import useOnClickOutside from 'hooks/useOnClickOutside';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';

export const MenuRoot = styled.div`
  position: relative;

  ${({ isStretched }) => css`
    ${isStretched &&
    css`
      height: 100%;
    `}
  `}
`;

const TippyToolTipContainer = styled.div`
  ${({ isSmallScreen, theme }) => css`
    ${!isSmallScreen &&
    css`
      margin-right: ${({ isMenuButtonStretched }) =>
        isMenuButtonStretched ? '0' : theme.spacing.xxsmall};
      height: 100%;
      display: flex;
      align-items: center;

      [data-tippy-root='true'] {
        height: 100%;
      }
    `}

    ${isSmallScreen &&
    css`
      ${MenuButton} {
        span {
          font-weight: normal;
        }
      }
    `}
  `}
`;

/**
 * Exported to apply custom focus- and hover-styles in child elements
 * Parent component can be used as a selector e.g. ${MenuButton}:hover &
 */
export const MenuButton = styled.button`
  padding: 0;
  height: 100%;
  width: 100%;
`;

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export default Menu;

/**
 * Menu is a composable and accessible dropdown component. It uses `downshift`
 * to manage focus state and screenreader communication. You can use this component
 * by providing a button component and list item component. These will be wrapped with
 * `downshift` logic and the needed props (e.g  `selectedItem` and `isOpen`) are passed
 * upwards to use in your custom button/list item.
 *
 * User interaction (focus, hover) happens on the `downshift` components.
 * To apply custom focus- and hover-styles in composed child components these parent components must be exported.
 * Custom focus- and hover-styles in child elements can be applied
 * via the parent selector e.g. ${ParentComponent}:hover &
 * The list-items, which can not be focused, can be styled with ${ParentComponent}[aria-selected='true'] &
 *
 * Note: don't pass `button` or `li`, these components are wrapped in those.
 * Note: all elements have a data key (e.g `data-menu-list`) so they can be styled
 *       from outside this component.
 *
 * Example components to pass to `Button`/`Item` props:
 * ```
 * function MenuButton(props) {
 *  const { selectedItem, isOpen } = props;
 *
 *  return (
 *    <ButtonContent isOpen={isOpen}>
 *      {selectedItem.label}
 *      <Chevron aria-hidden />
 *    </ButtonContent>
 *  );
 * }
 *
 *  function MenuItem(props) {
 *    const { item, isActive } = props;
 *
 *    return <ListItemContent isActive={isActive}>{item.label}</ListItemContent>;
 *  }
 * ```
 *
 * @param {Array} props.items array with shallow objects to be rendered as items
 * @param {Object} props.selectedItem currently active item
 * @param {Function} props.onChange callback when selectedItem changes
 * @param {Function} props.onSelect callback when selectedItem select
 * @param {Boolean} props.disabled should the dropdown be disabled?
 * @param {Function} props.Button button component
 * @param {Function} props.Item list item component
 * @param {Function} props.ListLabel optionally render a label for the list
 * @param {Function} props.children variantion of the menu to render (such as `SearchableMenu`)
 * @param {Function} props.itemToString callback to retrieve data string from the item object
 * @param {String} props.gtag Override default gtag for dropdown ('dropdown')
 * @param {boolean} props.isIconDisplayed if the customer icon should be displayed in chart tabs
 * @param {boolean} props.isDisplayedWithFlag if a flag should be displayed for Country Topic Filter Button
 * @param {String} props.disabledMessage reason why this dropdown is disabled
 */

const MenuButtonContainer = (props) => {
  const {
    children,
    isTooltipEnabled,
    tooltipContent,
    zIndex,
    maxWidth,
    placement,
    isMenuButtonStretched,
  } = props;

  if (tooltipContent && isTooltipEnabled)
    return (
      <TippyToolTipContainer
        isSmallScreen={false}
        isMenuButtonStretched={isMenuButtonStretched}
      >
        <TippyToolTip
          tooltipContent={tooltipContent}
          enabled={isTooltipEnabled}
          zIndex={zIndex}
          maxWidth={maxWidth}
          placement={placement}
          isBlockStretched={isMenuButtonStretched}
        >
          {children}
        </TippyToolTip>
      </TippyToolTipContainer>
    );

  return <>{children}</>;
};

function Menu(props) {
  const {
    items,
    onChange,
    onSelect,
    disabled,
    selectedItem,
    selectedItems,
    disabledMessage,
    Item,
    Button,
    ListLabel,
    itemToString,
    children,
    gtag = 'dropdown',
    isIconDisplayed,
    isDisplayedWithFlag,
    isToggleCaret,
    defaultButton,
    isOpenDropdownDef = false,
    onClose = noop,
    isActiveGreen,
    tooltipContent,
    maxWidth,
    zIndex = '99999',
    placement,
    isMenuButtonStretched,
    isTooltipEnabled,
    minHeight,
    placeholder,
    isMultipleSelection,
    isSubscriptionForm,
    onlyOneValue = false,
    onClickOutsideCustom,
    isRoundedDropdown,
    type,
  } = props;
  const [isOpenDropdown, toggleDropdownMenu] = useState(isOpenDropdownDef);

  function handleChange(selectedItem) {
    if (selectedItem) {
      onChange(selectedItem);
      if (!isMultipleSelection) {
        toggleDropdownMenu(false);
        onClose();
      }
    }
  }

  function handleSelect(selectedItem) {
    if (selectedItem && onSelect) {
      onSelect(selectedItem);
      if (!isMultipleSelection) {
        toggleDropdownMenu(false);
        onClose();
      }
    }
  }

  const menuRootRef = useRef(null);
  useOnClickOutside(menuRootRef, (e) => {
    // ignore click inside the tooltip
    if (
      e?.path?.[6]?.id?.match(/tippy/gi) ||
      e?.path?.[7]?.id?.match(/tippy/gi)
    ) {
      return;
    }
    if (isOpenDropdown) {
      toggleDropdownMenu(false);
      onClose();
      if (onClickOutsideCustom) {
        onClickOutsideCustom();
      }
    }
  });

  return (
    <Downshift
      itemToString={itemToString}
      onChange={handleChange}
      onSelect={handleSelect}
      selectedItem={selectedItems ? undefined : selectedItem}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        getRootProps,
        selectedItem,
        highlightedIndex,
        setHighlightedIndex,
        selectHighlightedItem,
      }) => (
        <MenuRoot
          {...getRootProps({ 'data-menu-root': true })}
          data-gtag={gtag}
          ref={menuRootRef}
          isStretched={
            (isTooltipEnabled && tooltipContent) || isOpenDropdownDef
          }
          data-pw="menu-root"
        >
          <MenuButtonContainer
            tooltipContent={tooltipContent}
            isTooltipEnabled={isTooltipEnabled}
            zIndex={zIndex}
            maxWidth={maxWidth}
            placement={placement}
            isMenuButtonStretched={isMenuButtonStretched}
          >
            <MenuButton
              {...getInputProps({
                'aria-haspopup': true,
                'aria-expanded': isOpenDropdown,
                'data-menu-button': true,
                type: 'button',
                disabled: disabled && !disabledMessage,
                onClick: () => {
                  if (!onlyOneValue) {
                    toggleDropdownMenu(!isOpenDropdown);
                    onClose();
                  }
                },
              })}
            >
              <Button
                selectedItem={
                  props?.selectedItems
                    ? props?.selectedItem
                    : selectedItem || defaultButton
                }
                selectedItems={props?.selectedItems}
                isOpen={isOpenDropdown}
                disabled={disabled}
                isIconDisplayed={isIconDisplayed}
                isDisplayedWithFlag={isDisplayedWithFlag}
                isToggleCaret={isToggleCaret}
                isActiveGreen={isActiveGreen}
                minHeight={minHeight}
                placeholder={placeholder}
                isSubscriptionForm={isSubscriptionForm}
                isRoundedDropdown={isRoundedDropdown}
                type={type}
              />
            </MenuButton>
          </MenuButtonContainer>
          {isOpenDropdown &&
            children({
              items,
              selectedItem,
              Item,
              ListLabel,
              itemToString,
              getMenuProps,
              getItemProps,
              getInputProps,
              highlightedIndex,
              setHighlightedIndex,
              selectHighlightedItem,
            })}
        </MenuRoot>
      )}
    </Downshift>
  );
}

Menu.defaultProps = {
  /** This function finds the unique key in the item object, on which item comparison can happen. */
  itemToString: (item) => item && item.value,
};

Menu.propTypes = {
  items: oneOfType([arrayOf(object), arrayOf(string)]).isRequired,
  selectedItem: oneOfType([object, string]),
  selectedItems: any,
  onChange: func.isRequired,
  onSelect: func,
  disabled: bool,
  disabledMessage: string,
  Button: func.isRequired,
  Item: func.isRequired,
  ListLabel: any,
  itemToString: func,
  children: func.isRequired,
  gtag: string,
  isOpen: bool,
  isIconDisplayed: bool,
  isDisplayedWithFlag: bool,
  isToggleCaret: bool,
  defaultButton: object,
  isOpenDropdownDef: bool,
  isActiveGreen: bool,
  onClose: func,
  tooltipContent: oneOfType([object, string]),
  isTooltipEnabled: bool,
  /** Max width of the tooltip for the dropdown, zIndex of the tooltip block,
   * placement is for place of tooltip relatively the block, isStretched is to stretch the block in tooltip wrapper */
  maxWidth: string,
  zIndex: string,
  placement: string,
  isMenuButtonStretched: bool,
  minHeight: string,
  placeholder: string,
  isMultipleSelection: bool,
  isSubscriptionForm: bool,
  onlyOneValue: bool,
  onClickOutsideCustom: func,
  isRoundedDropdown: string,
};
